import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlanStep, SelectedPlanBar } from "../components/PlanStep";
import { SignStep } from "../components/SignStep";
import { ReviewStep } from "../components/ReviewStep";
import { PayStep } from "../components/PayStep";
import { DoneStep } from "../components/DoneStep";
import {
  createPendingPaymentIntent,
  finalizePendingSignup,
  getPublicOnboardingConfig,
  type BillingPlan,
  type ExhibitAPayload,
  type FinalizePendingSignupResp,
  type OnboardingPrefill,
  type OnboardingPricing,
  type ProbationOfficerSummary,
} from "../lib/api";

/**
 * Snapshot of the data collected on the /signup form, handed off from
 * SignupPage. Zero side-effects have run on the backend at this point.
 */
export interface PendingSignup {
  email: string;
  displayName: string;
  county: string;
  state: string;
  /** Selected officer, if any (used to prefill + link on the eventual user doc). */
  officer: null | ProbationOfficerSummary;
}

type Step =
  | "loading"
  | "plan"
  | "sign"
  | "review"
  | "pay"
  | "finalizing"
  | "complete"
  | "error";

interface Props {
  pendingSignup: PendingSignup;
  /** True when the mobile app opened /signup?from=app. */
  openedFromApp?: boolean;
}

/**
 * Self-signup onboarding flow that DOESN'T create an account until payment
 * succeeds. Runs the same Plan → Sign → Review → Pay → Done sequence as the
 * authenticated `OnboardingPage`, but:
 *   - The lease template URLs + pricing come from the public config callable.
 *   - The PayStep uses `createPendingPaymentIntent` (which persists the
 *     signed lease server-side keyed by PI id).
 *   - After payment succeeds we call `finalizePendingSignup`, which creates
 *     the Auth user + Firestore user + Stripe subscription in one shot.
 *   - A Stripe webhook finalizes as a safety net if the browser dies.
 */
export function PendingOnboardingPage({
  pendingSignup,
  openedFromApp = false,
}: Props) {
  const [step, setStep] = useState<Step>("loading");
  const [config, setConfig] = useState<{
    leaseTemplateUrls: Record<BillingPlan, string>;
    pricing: OnboardingPricing;
  } | null>(null);
  const [billingPlan, setBillingPlan] = useState<BillingPlan | null>(null);
  const [signedPdfBase64, setSignedPdfBase64] = useState<string>("");
  const [signerFullName, setSignerFullName] = useState<string>("");
  const [exhibitA, setExhibitA] = useState<ExhibitAPayload | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string>("");
  const [finalized, setFinalized] = useState<FinalizePendingSignupResp | null>(
    null,
  );
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [partnerName, setPartnerName] = useState<string | null>(null);

  // Fetch the lease template URL + pricing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await getPublicOnboardingConfig();
        if (cancelled) return;
        setConfig(c);
        setStep("plan");
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setFatalError(String((err as Error)?.message ?? err));
          setStep("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const prefill = useMemo<OnboardingPrefill>(
    () => ({
      client: {
        fullName: pendingSignup.displayName,
        dateOfBirth: null,
        addressLine: null,
        city: null,
        state: pendingSignup.state,
        zipcode: null,
        phone: null,
        email: pendingSignup.email,
      },
      work: { employerName: null, employerAddress: null, employerPhone: null },
      probationOfficers: pendingSignup.officer ? [pendingSignup.officer] : [],
    }),
    [pendingSignup],
  );

  const handlePlanSelected = useCallback((plan: BillingPlan) => {
    setBillingPlan(plan);
    setStep("sign");
  }, []);

  const handleSigned = useCallback(
    (out: {
      signedPdfBase64: string;
      signerFullName: string;
      exhibitA: ExhibitAPayload;
    }) => {
      setSignedPdfBase64(out.signedPdfBase64);
      setSignerFullName(out.signerFullName);
      setExhibitA(out.exhibitA);
      setStep("review");
    },
    [],
  );

  const handleReviewed = useCallback(() => {
    if (!signedPdfBase64 || !signerFullName || !exhibitA) {
      setStep("sign");
      return;
    }
    setStep("pay");
  }, [signedPdfBase64, signerFullName, exhibitA]);

  const handleBackToSign = useCallback(() => {
    setSignedPdfBase64("");
    setExhibitA(null);
    setStep("sign");
  }, []);

  // Memoize the intent creator so PayStep doesn't refire on every render.
  // Uses the latest signed PDF + exhibit A via refs so coupon toggles after
  // a re-sign still send the correct payload.
  const signedRef = useRef({
    signedPdfBase64,
    signerFullName,
    exhibitA,
    billingPlan,
  });
  useEffect(() => {
    signedRef.current = { signedPdfBase64, signerFullName, exhibitA, billingPlan };
  }, [signedPdfBase64, signerFullName, exhibitA, billingPlan]);

  const createIntent = useCallback(
    async (opts: { couponCode?: string; existingPaymentIntentId?: string }) => {
      const { signedPdfBase64, signerFullName, exhibitA, billingPlan } =
        signedRef.current;
      if (!signedPdfBase64 || !signerFullName || !exhibitA || !billingPlan) {
        throw new Error(
          "Signed lease is missing — please go back and sign again.",
        );
      }
      const resp = await createPendingPaymentIntent({
        email: pendingSignup.email,
        displayName: pendingSignup.displayName,
        county: pendingSignup.county,
        state: pendingSignup.state,
        ...(pendingSignup.officer?.uid
          ? { officerUid: pendingSignup.officer.uid }
          : {}),
        signedPdfBase64,
        signerFullName,
        exhibitA,
        billingPlan,
        ...(opts.existingPaymentIntentId
          ? { paymentIntentId: opts.existingPaymentIntentId }
          : {}),
        ...(opts.couponCode ? { couponCode: opts.couponCode } : {}),
      });
      // Remember the PI id so we know what to finalize.
      setPaymentIntentId(resp.paymentIntentId);
      setPartnerName(
        resp.checkoutMode === "setup" ? (resp.partnerName ?? null) : null,
      );
      return {
        clientSecret: resp.clientSecret,
        paymentIntentId: resp.paymentIntentId,
        status: resp.status,
        amount: resp.amount,
        currency: resp.currency,
        couponApplied: resp.couponApplied,
        activationFeeWaivedCents: resp.activationFeeWaivedCents,
        checkoutMode: resp.checkoutMode ?? "payment",
        partnerName: resp.partnerName ?? null,
        partnerCode: resp.partnerCode ?? null,
      };
    },
    [pendingSignup],
  );

  // After Stripe confirms payment, finalize on the backend. Idempotent, so
  // safe to retry — the "aborted" error just means the webhook is running
  // concurrently and we should try again in a moment.
  const runFinalize = useCallback(async (piId: string) => {
    setFinalizeError(null);
    const maxAttempts = 6;
    let attempt = 0;
    while (attempt < maxAttempts) {
      attempt += 1;
      try {
        const resp = await finalizePendingSignup(piId);
        setFinalized(resp);
        setStep("complete");
        return;
      } catch (err) {
        const code =
          (err as { code?: string })?.code ??
          (err as { details?: { code?: string } })?.details?.code ??
          "";
        const message = String((err as Error)?.message ?? err);
        const isRetryable =
          code.includes("aborted") ||
          /already in progress/i.test(message);
        if (!isRetryable || attempt >= maxAttempts) {
          console.error("finalizePendingSignup failed", err);
          setFinalizeError(message);
          setStep("error");
          return;
        }
        // Back off: 1.5s, 3s, 4.5s, 6s...
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }
  }, []);

  const handlePaid = useCallback(
    async (piId: string) => {
      setPaymentIntentId(piId);
      setStep("finalizing");
      await runFinalize(piId);
    },
    [runFinalize],
  );

  if (step === "loading") {
    return (
      <div className="py-16 text-center text-sm text-slate-500">
        Loading your enrollment…
      </div>
    );
  }

  if (step === "error" && !finalizeError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5">
        <h2 className="text-lg font-semibold text-red-900">
          We couldn't load your enrollment
        </h2>
        <p className="mt-1 text-sm text-red-800">{fatalError}</p>
        <p className="mt-3 text-sm text-red-800">
          Please refresh the page or try again in a moment. If the problem
          persists, contact AMS support.
        </p>
      </div>
    );
  }

  if (step === "error" && finalizeError) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <h2 className="text-lg font-semibold text-red-900">
            We hit a snag finalizing your enrollment
          </h2>
          <p className="mt-1 text-sm text-red-800">{finalizeError}</p>
          <p className="mt-3 text-sm text-red-800">
            Your payment was processed. You'll receive your sign-in credentials
            by email shortly — no need to pay again. If the email doesn't
            arrive in 10 minutes, contact AMS support and reference Payment{" "}
            {paymentIntentId}.
          </p>
        </div>
        {paymentIntentId ? (
          <button
            type="button"
            onClick={() => {
              setStep("finalizing");
              void runFinalize(paymentIntentId);
            }}
            className="w-full rounded-lg bg-brand-500 px-4 py-3 text-white font-semibold hover:bg-brand-600"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-8">
      <Stepper current={step} />

      {step === "plan" ? (
        <PlanStep
          pricing={config.pricing}
          initialPlan={billingPlan}
          onSelected={handlePlanSelected}
        />
      ) : null}

      {step === "sign" && billingPlan ? (
        <>
          <SelectedPlanBar
            plan={billingPlan}
            pricing={config.pricing}
            onChange={setBillingPlan}
          />
          <SignStep
            leaseTemplateUrl={config.leaseTemplateUrls[billingPlan]}
            initialFullName={pendingSignup.displayName}
            prefill={prefill}
            onSigned={handleSigned}
          />
        </>
      ) : null}

      {step === "review" && signedPdfBase64 ? (
        <ReviewStep
          signedPdfBase64={signedPdfBase64}
          onContinue={handleReviewed}
          onBack={handleBackToSign}
        />
      ) : null}

      {step === "pay" && billingPlan ? (
        <PayStep
          pricing={config.pricing}
          billingPlan={billingPlan}
          createIntent={createIntent}
          onPaid={handlePaid}
        />
      ) : null}

      {step === "finalizing" ? (
        <div className="py-16 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          <p className="mt-4 text-sm text-slate-600">
            {partnerName
              ? "Card saved — setting up your account…"
              : "Payment received — setting up your account…"}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            This takes a few seconds. Please don't close the page.
          </p>
        </div>
      ) : null}

      {step === "complete" && finalized ? (
        <DoneStep
          uid={finalized.uid}
          token={finalized.token}
          signedPdfBase64={signedPdfBase64}
          signerFullName={signerFullName}
          exhibitA={exhibitA}
          paymentIntentId={paymentIntentId}
          loginEmail={finalized.email}
          password={finalized.password}
          alreadyComplete
          partnerName={partnerName}
          pricing={config.pricing}
          billingPlan={billingPlan}
          testingDeferred
          openedFromApp={openedFromApp}
        />
      ) : null}
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  const labels: { id: Step; label: string }[] = [
    { id: "plan", label: "Plan" },
    { id: "sign", label: "Sign" },
    { id: "review", label: "Review" },
    { id: "pay", label: "Payment" },
    { id: "complete", label: "Done" },
  ];
  // "finalizing" visually sits between Pay and Done.
  const effective: Step = current === "finalizing" ? "complete" : current;
  const currentIdx = labels.findIndex((l) => l.id === effective);
  return (
    <ol className="flex items-center justify-center gap-2 text-xs sm:text-sm">
      {labels.map((l, i) => {
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <li key={l.id} className="flex items-center gap-2">
            <span
              className={
                "flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold " +
                (isDone
                  ? "bg-brand-500 border-brand-500 text-white"
                  : isActive
                    ? "border-brand-500 text-brand-600 bg-white"
                    : "border-slate-300 text-slate-400 bg-white")
              }
            >
              {isDone ? "✓" : i + 1}
            </span>
            <span
              className={
                isActive
                  ? "text-slate-900 font-medium"
                  : isDone
                    ? "text-slate-600"
                    : "text-slate-400"
              }
            >
              {l.label}
            </span>
            {i < labels.length - 1 ? (
              <span className="mx-2 h-px w-6 bg-slate-300" aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
